import clsx from 'clsx'
import { Link } from '@tanstack/react-router'
import { useState, useMemo, createContext, DependencyList, ReactNode, useContext, useEffect } from 'react'
import { useAuth } from '@/auth/AuthContext'
import { Can, useAccessContext } from '@/auth/access'
import { canAccessUsersSection, canAccessCompaniesSection, canAccessStaffSection, isSystemContext } from '@tandem/schema/permissions'
import $ from './Main.css'
import Logo from '@tandem/ui/Тандем.svg?react'
import Dropdown from '@tandem/ui/Dropdown'
import md5 from 'md5'
import { Briefcase as BriefcaseIcon, Building2Icon, PackageIcon, Users2Icon, UsersRoundIcon } from 'lucide-react'


export default function Layout({ children }) {
  let { user, logout, company, companies, setActiveCompanyId } = useAuth()
  let userEmail = user?.email || ''
  let gravatarUrl = getGravatarUrl(userEmail)
  let inSystemContext = isSystemContext(useAccessContext())

  let [menuOpen, setMenuOpen] = useState(false)
  let [sidecar, setSidecar] = useState<ReactNode>(null)
  let sidecarContextValue = useMemo(() => ({ content: sidecar, setContent: setSidecar }), [sidecar])

  return (
    <SidecarContext.Provider value={sidecarContextValue}>
      <div class={$.layout}>
        
        <header class={$.header}>
          <div class={$.logoWrapper}>{/* Side elements of header have flex-grow:1 making SVG centerified w/o wrapper. */}
            <Link to='/'>
              <Logo class={$.logo} />
            </Link>
          </div>
          
          <ul class={$.nav}>
            <Can check={canAccessUsersSection}>
              <li>
                <Link to='/users' activeProps={{ className: $.active }} className={clsx($.link, $.system)}>
                  <Users2Icon />
                  <span>Пользователи</span>
                </Link>
              </li>
            </Can>
            <Can check={canAccessCompaniesSection}>
              <li>
                <Link to='/companies' activeProps={{ className: $.active }} className={clsx($.link, $.system)}>
                  <Building2Icon />
                  <span>Компании</span>
                </Link>
              </li>
            </Can>
            <li>
              <Link to='/catalog' activeProps={{ className: $.active }} className={$.link}>
                <PackageIcon />
                <span>Каталог</span>
              </Link>
            </li>
            <Can check={canAccessStaffSection}>
              <li>
                <Link to='/staff' activeProps={{ className: $.active }} className={$.link}>
                  <UsersRoundIcon />
                  <span>Сотрудники</span>
                </Link>
              </li>
            </Can>
            <li>
              <Link to='/proexpert' activeProps={{ className: $.active }} className={$.link}>
                <BriefcaseIcon />
                <span>Эксперты</span>
              </Link>
            </li>
          </ul>

          <Dropdown
            open={menuOpen}
            onOpenChange={setMenuOpen}
            align='left'
            verticalAlign='bottom'
            trigger={
              <button class={$.userMenuTrigger} type='button'>
                <img src={gravatarUrl} class={clsx($.userpic, inSystemContext && $.system)} />
              </button>
            }
            class={$.authDropdown}
          >
            <div class={$.userMenu}>
              {companies.length > 0 && (
                <>
                  {companies.map((c) => (
                    <button
                      key={c.company_id}
                      type='button'
                      class={clsx(
                        $.menuOption,
                        c.company_id === company?.company_id && $.menuOptionActive,
                      )}
                      onClick={() => {
                        setActiveCompanyId(c.company_id)
                        setMenuOpen(false)
                      }}
                    >
                      <span>{c.name}</span>
                    </button>
                  ))}
                  <hr class={$.menuDivider} />
                </>
              )}
              <Link to='/settings' className={$.menuOption} activeProps={{ className: $.menuOptionActive }} onClick={() => setMenuOpen(false)}>
                Настройки
              </Link>
              <button
                class={$.menuOption}
                type='button'
                onClick={() => {
                  logout()
                  setMenuOpen(false)
                }}
              >
                Выйти
              </button>
            </div>
          </Dropdown>
        </header>

        <div class={$.body}>
          <main class={$.content}>{children}</main>
          {sidecar && <aside class={$.sidecar}>{sidecar}</aside>}
        </div>

        <footer class={$.footer}>© 2026 ООО «Тандем Ритейл»</footer>
      </div>
    </SidecarContext.Provider>
  )
}


export let SidecarContext = createContext<{
  content: ReactNode
  setContent: (node: ReactNode) => void
} | null>(null)


export function useSidecar(renderContent: () => ReactNode, deps: DependencyList) {
  let context = useContext(SidecarContext)
  useEffect(() => {
    context?.setContent(renderContent())
    return () => context?.setContent(null)
  }, deps || [])
}


function getGravatarUrl(email: string, size = 80) {
  let hash = md5(email.toLowerCase().trim())
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=identicon`
}
